<template>
    <Sidebar :show="show && !loading" width="650px">
        <div class="row pt-3 px-3 pb-0">
            <div class="col-12 d-flex flex-row align-items-center">
                <div class="mr-auto font-weight-bold text-sm">{{ $t('recur-settings') }}</div>
                <button 
                    type="button" 
                    class="btn btn-outline-secondary btn-sm"
                    @click="$emit('close')"
                >
                    {{  $t('back') }}
                </button>
            </div>
        </div>

        <!-- Recur Event Set -->
        <div class="row px-3 pb-3">
            <div class="col-12 d-flex align-items-end">
                <template v-if="recurEventSet === null">
                    <TextInput :label="$t('title')" v-model="newTitle" class="w-75 mb-0" />
                    <button 
                        type="button" 
                        class="btn btn-primary ml-2 w-25"
                        @click="saveSet"
                    >
                        {{ $t('create') }}
                    </button>
                </template>
                <template v-else>
                    <TextInput :label="$t('title')" v-model="recurEventSet.title" class="w-75 mb-0" />
                    <button 
                        type="button" 
                        class="btn btn-primary mx-2 w-25"
                        @click="saveSet"
                    >
                        {{ $t('save') }}
                    </button>
                    <button class="btn btn-outline-danger" @click="showDeleteSetDialog">
                        <b-icon icon="trash" />
                    </button>
                </template>
            </div>
            <div class="col-12">
                <div class="mr-auto font-weight-bold text-sm mt-3">{{ $t('events') }}</div>
            </div>
            <div class="col-12 pt-3 d-flex justify-content-between align-items-center">
                <YesNoToggle 
                    v-model="rotations"
                    @input="resetSelected"
                    :yesLabel="$t('rotations')"
                    :noLabel="$t('events')"
                    :label="$t('editing')"
                    :srOnly="true"
                    :noFirst="true"
                    class="mb-0"
                />
                <button 
                    v-show="editRecur"
                    type="button" 
                    class="btn btn-dark"
                    @click="editRecur = false"
                >
                    {{ $t('back') }}
                </button>
            </div>
        </div>

        <div v-if="recurEventSet" class="row px-3 pb-3">
            <div
                v-if="from && until && groups.length && editRecur"
                class="col-12"
                ref="editor"
            >
                <TextInput v-model="selectedRecur.title" :label="$t('title')" />

                <!-- Date, From - Until -->
                <div class="d-flex flex-column mb-2">
                    <DateInput 
                        :label="$t('date')" 
                        v-model="selectedRecur.date" 
                        class="flex-grow-1" 
                        :min="from"
                        :max="until"
                    />
                    <TimeInput :label="$t('from')" v-model="selectedRecur.from" class="flex-grow-1" />
                    <TimeInput :label="$t('until')" v-model="selectedRecur.to"  class="flex-grow-1"/>
                </div>

                <div class="d-flex flex-column mb-2">
                    <!-- recur event session type -->
                    <SelectInput 
                        v-if="!rotations"
                        v-model="selectedRecur.ttsessiontype"
                        :label="$t('session-types')"
                        :options="sessionTypes" 
                        optionLabel="name"
                    />
                    <!-- rotation session-type multi-select -->
                    <div v-else class="d-flex align-items-end mb-3" style="gap: 0.5rem">
                        <SelectInput 
                            v-model="selectedRecur.ttsessiontype"
                            :label="$t('session-types')"
                            :options="sessionTypes" 
                            optionLabel="name"
                            class="mb-0"
                            :nullable="true"
                            :nullLabel="$t('select')"
                        />
                        <NumberInput 
                            v-model="sessionSize" 
                            :disabled="!sessionSize"
                            :min="1" 
                            :max="100" 
                            :nullable="true"
                            :nullLabel="$t('remaining')"
                            :label="$t('size')" 
                            class="mb-0"
                        />
                        <button 
                            type="button" 
                            class="btn btn-outline-primary text-nowrap" 
                            @click="addRotationSessionType"
                            :disabled="!selectedRecur.ttsessiontype"
                        >
                            <b-icon icon="plus" /> {{ $t('add') }}
                        </button>
                        <CheckboxInput 
                            :value="!sessionSize" 
                            @input="sessionSize = (sessionSize ? '' : 1)"
                            :label="$t('allocate-remaining')"
                            class="ml-2 mb-2"
                        />
                    </div>

                    <div v-if="rotations" class="mb-3">
                        <div 
                            v-for="sessionType in selectedRecur.ttsessiontypes" 
                            :key="sessionType.id"
                            :style="{
                                color: getTextColor(sessionType),
                                backgroundColor: sessionType.colourcode ? `#${sessionType.colourcode}` : undefined,
                            }"
                            class="p-1 mb-1 rounded border d-flex align-items-center"
                        >
                            {{ sessionType.name }} {{ Number(sessionType.size) ? ` - ${sessionType.size} ${$t('students')}` : '' }}
                            <span class="badge badge-light ml-auto p-0" @click="selectedRecur.ttsessiontypes = selectedRecur.ttsessiontypes.filter(st => st.id != sessionType.id)">
                                <b-icon icon="x" class="h5 mb-0"/>
                            </span>
                        </div>
                    </div>

                    <SelectInput 
                        v-if="programme"
                        v-model="selectedRecur.ttroom"
                        :label="$t('room')"
                        :options="selectedRecur.ttsessiontype ? selectedRecur.ttsessiontype.ttrooms : rotationRooms"
                        optionLabel="name"
                    />
                </div>
                <!-- Groups -->
                <GroupMultiSelect
                    v-if="selectedRecur.groups.length || groups.length"
                    :value="selectedRecur.groups"
                    :groups="groups.map(gr => (gr.group_id ? gr : {...gr, group_id: gr.id, programme_id: programme.Id}))" 
                    class="mb-3"
                    :monoProgramme="true"
                    v-model="selectedRecur.groups"
                />

                <!-- Recurring Event? -->
                <div>
                    <CheckboxInput v-model="selectedRecur.recur" :label="$t('recur')" :class="{'mb-0': !selectedRecur.recur}" />

                    <template v-if="selectedRecur.recur">
                        <SelectInput
                            :label="$t('period')"
                            v-model="selectedRecur.recurSettings.period"
                            :options="periodLabels"
                            :indexIsValue="true"
                            :nullable="false"
                        />

                        <!-- Recur Limit? -->
                        <div class="d-flex flex-wrap align-items-start" style="gap: 1rem;">
                            <NumberInput 
                                v-show="selectedRecur.recurSettings.limitType" 
                                :label="$t('for')" 
                                v-model="selectedRecur.recurSettings.limitSize" 
                                :min="1" 
                                class="mb-0"
                            />
                            <SelectInput 
                                :label="$t('recur-limit')"
                                v-model="selectedRecur.recurSettings.limitType"
                                :options="{'WEEKS': $t('weeks'), 'EVENTS': $t('events'), 'DAYS': $t('days')}"
                                :indexIsValue="true"
                                :nullLabel="$t('none')"
                            />
                        </div>
                    </template>
                </div>

                <div class="d-flex w-100 my-3 align-items-center">
                    <button 
                        type="button" 
                        class="btn btn-outline-primary flex-grow-1"
                        @click="saveRecurEvent"
                    >
                        {{ selectedRecur.id ? $t('save') : $t('create') }}
                    </button>
                    <button v-if="selectedRecur.id" class="btn btn-outline-danger ml-2" @click="showDeleteEventDialog">
                        <b-icon icon="trash" />
                    </button>
                </div>
            </div>

            <div v-else class="col-12">
                <button 
                    type="button" 
                    class="border rounded p-2 mb-2 shadow-sm d-flex align-items-center w-100 bg-white" 
                    @click="selectNew"
                >
                    <b-icon icon="arrow-left" class="mr-2" />{{ rotations ? $t('new-rotation') : $t('new-recur-event') }}
                </button>
                <div 
                    v-for="recurEvent in Object.values(rotations ? rotationsById : recurEventsById)" 
                    :key="recurEvent.id" 
                    class="border rounded p-2 mb-2 shadow-sm d-flex flex-column"
                    style="cursor: pointer;"
                    @click="() => {
                        recurEvent.ttsessiontypes ? recurEvent.ttsessiontypes.forEach(t => {
                            selectRecur({
                                ...recurEvent,
                                uid: `${recurEvent.id}-${t.id}`,
                                ttsessiontype: t,
                            })
                        }) : selectRecur(recurEvent)
                    }"
                >
                    <div class="mb-2">
                        {{ recurEvent.date.split('-').reverse().join('/') }}
                    </div>
                    <template v-if="recurEvent.ttsessiontypes">
                        <TimetableEvent 
                            v-for="sessionType in recurEvent.ttsessiontypes" 
                            :key="`${recurEvent.id}-${sessionType.id}`"
                            :event="{
                                ...recurEvent,
                                ttsessiontype: sessionType,
                            }"
                            class="p-1 mb-1 text-sm border"
                        />
                    </template>
                    <TimetableEvent 
                        v-else 
                        :key="recurEvent.id"
                        :event="recurEvent"
                        class="p-1 mb-1 text-sm border"
                    />
                    <div>
                        {{ periodLabels[recurEvent.recurSettings.period] }} - 
                        {{ $t('for-{n}-{interval}', { 
                            n: recurEvent.recurSettings.limitSize,
                            interval: recurEvent.recurSettings.limitType === 'WEEKS' ? $t('weeks').toLowerCase() : $t('days').toLowerCase(),
                        }) }}
                    </div>
                </div>
            </div>
        </div>
    </Sidebar>
</template>

<script>
import Sidebar from "src/components/Util/Sidebar.vue"
import GroupMultiSelect from 'src/components/ProgrammeYearGroups/GroupMultiSelect.vue'
import DateInput from 'src/components/Inputs/DateInput.vue'
import TimeInput from 'src/components/Inputs/TimeInput.vue'
import CheckboxInput from 'src/components/Inputs/CheckboxInput.vue'
import NumberInput from 'src/components/Inputs/NumberInput.vue'
import TimetableEvent from "src/components/Timetables/TimetableEvent.vue"
import YesNoToggle from 'src/components/Inputs/YesNoToggle.vue'

import UUID from 'src/modules/uuid'
import {
    dateFromISODate,
    dateToISODate,
    isoTimeFromTimestamp,
    isoDateDifference,
    getTextColor,
} from "src/modules/timetable/util"

export default {
    components: {
        Sidebar,
        GroupMultiSelect,
        DateInput,
        TimeInput,
        CheckboxInput,
        NumberInput,
        TimetableEvent,
        YesNoToggle,
    },
    props: {
        show: {
            type: Boolean,
            default: false,
        },
        programme: {
            type: Object,
            default: () => ({})
        },
        grouptypeId: [String, Number],
        groups: {
            type: Array,
            default: () => [],
        },
        recurEventSet: {
            type: Object,
            default: null,
        },
        from: String,
        until: String,
    },
    mounted() {
        Promise.all([
            this.fetchSessionTypes(), 
            this.fetchRecurEvents(),
            this.fetchRotations(),
        ]).then(() => {
            this.loading = false
        })
    },
    data() {
        return {
            loading: true,
            submitting: false,
            rotations: false,
            editRecur: false,
            newTitle: '',
            selectedRecur: {
                id: 0,
                title: '',
                date: '',
                from: '',
                to: '',
                ttsessiontype: null,
                ttroom: null,
                recur: true,
                groups: [],
                students: [],
                recurSettings: {
                    period: 7,
                    limitType: 'WEEKS', // null, 'WEEKS', 'EVENTS', 'DAYS'
                    limitSize: 1,
                },

                // Rotations
                ttsessiontypes: [],
            },
            sessionTypes: [],
            recurEventSets: [],
            recurEventsById: {},
            periodLabels: {
                7: this.$t('every-week'), 
                14: this.$t('every-two-weeks'), 
                28: this.$t('every-four-weeks'), 
                1: this.$t('daily')
            },

            // Rotations
            sessionSize: 1,
            rotationsById: {},
            rotationRooms: [],
        }
    },
    methods: {
        saveSet(blocking=true) {
            if (blocking) {
                if (this.submitting) return Promise.resolve()
                this.submitting = true
                this.$store.commit('showBlockingSpinner')
            }

            const creating = this.recurEventSet === null
            const data = creating 
            ? { 
                title: this.newTitle,
                datefrom: this.from,
                dateto: this.until,
                grouptype_id: this.grouptypeId,
            } : {
                ...this.recurEventSet,
                datefrom: this.from,
                dateto: this.until,
                grouptype_id: this.grouptypeId,
            }

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/save`, {
                method: 'POST',
                data
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                if (creating) this.$emit('newSet', res.result)
                else Object.keys(res.result).forEach(k => this.recurEventSet[k] = res.result[k])
            }).catch(err => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        fetchRecurEvents() {
            if (!this.recurEventSet) return

            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/recurevents/all`, {
                method: 'GET',
                data: {
                    ttrecureventset_id: this.recurEventSet.id
                },
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res
                
                Object.values(this.recurEventsById).forEach(e => this.$emit('applyRecur', e.id, []))
                this.recurEventsById = this.processEvents(res.results)
                this.resetSelected()
            }).catch(err => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        fetchRotations() {
            if (!this.recurEventSet) return

            this.$store.commit('showBlockingSpinner')

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/rotations/all`, {
                method: 'GET',
                data: {
                    ttrecureventset_id: this.recurEventSet.id
                },
            }).then(res => {
                if (res.status != 'SUCCESS') throw res

                Object.values(this.rotationsById).forEach(r => {
                    r.ttsessiontypes.forEach(t => {
                        this.$emit('applyRecur', `${r.id}-${t.id}`, [])
                    })
                })

                this.rotationsById = this.processRotations(res.results)
                this.resetSelected()
            }).catch(err => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
            })
        },
        fetchSessionTypes() {
            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/sessiontypes/all`, {
                method: 'GET',
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.sessionTypes = res.results
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-fetching-session-types'),
                })
            })
        },
        processEvents(events) {
            const startDate = dateFromISODate(this.from)

            return events.map(e => {
                const date = new Date(startDate)
                date.setDate(date.getDate() + Number(e.offset))

                e.date = dateToISODate(date)
                e.recurSettings = this.deriveSettings(e)
                e.uid = `${e.id}`

                this.selectRecur(e)
                return e
            }).reduce((prev, curr) => {
                prev[curr.id] = curr
                return prev
            }, {})
        },
        processRotations(rotations) {
            const startDate = dateFromISODate(this.from)

            return rotations.map(r => {
                const date = new Date(startDate)
                date.setDate(date.getDate() + Number(r.offset))

                r.date = dateToISODate(date)
                r.recurSettings = this.deriveSettings(r)

                r.ttsessiontypes.forEach(type => {
                    const e = {
                        ...r,
                        ttsessiontype: type,
                        ttsessiontype_id: type.id,
                        uid: `${r.id}-${type.id}`
                    }

                    this.selectRecur(e)
                })

                return r
            }).reduce((prev, curr) => {
                prev[curr.id] = curr
                return prev
            }, {})
        },
        saveRecurEvent() {
            if (this.submitting || !this.recurEventSet) return Promise.resolve()
            if (!this.recurEventValid()) return

            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            let limit
            switch (this.selectedRecur.recurSettings.limitType) {
                case null:
                    break
                case 'WEEKS':
                    limit = Math.ceil((this.selectedRecur.recurSettings.limitSize * 7) / this.selectedRecur.recurSettings.period)
                    break
                case 'DAYS':
                    limit = Math.ceil(this.selectedRecur.recurSettings.limitSize / this.selectedRecur.recurSettings.period)
                    break
                case 'EVENTS':
                    limit = this.selectedRecur.recurSettings.limitSize
                    break
                default:
                    limit = null
                    break
            }

            const data = {
                id: this.selectedRecur.id,
                title: this.selectedRecur.title,
                date: this.selectedRecur.date,
                from: this.selectedRecur.from,
                to: this.selectedRecur.to,
                ttrecureventset_id: this.recurEventSet.id,
                ttroom_id: this.selectedRecur.ttroom ? this.selectedRecur.ttroom.id : undefined,
                period: this.selectedRecur.recurSettings.period,
                limit,
                offset: isoDateDifference(this.from, this.selectedRecur.date),
                groups: this.selectedRecur.groups.map(gr => ({
                    ...gr,
                    id: gr.group_id,
                })),
            }

            if (this.rotations) data.ttsessiontypes = this.selectedRecur.ttsessiontypes
            else data.ttsessiontype_id = this.selectedRecur.ttsessiontype.id

            return this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/${this.rotations ? 'rotations' : 'recurevents'}/save`, {
                method: 'POST',
                data,
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                const date = dateFromISODate(this.from)
                date.setDate(date.getDate() + res.result.offset)

                if (this.rotations) {
                    this.rotationsById[res.result.id] = res.result
                    this.processRotations([res.result])    
                } else {
                    this.recurEventsById[res.result.id] = res.result
                    this.processEvents([res.result])
                }
            }).catch(err => {
                this.$log.error.call(this, err)
            }).finally(() => {
                this.submitting = false
                this.$store.commit('hideBlockingSpinner')
            })
        },
        applyRecurEvent() {
            const events = []

            this.selectedRecur.students = this.selectedRecur.groups.reduce((res, cur) => { 
                if (!cur.students) return res

                res.push(...cur.students)
                return res
            }, [])

            const fromParts = this.selectedRecur.from.split(':')
            const toParts = this.selectedRecur.to.split(':')

            if (!this.selectedRecur.recur) {
                const date = dateFromISODate(this.selectedRecur.date)

                const from = (new Date(date).setHours(...fromParts).valueOf() - (date.getTimezoneOffset() * 60000))/1000
                const to = (new Date(date).setHours(...toParts).valueOf() - (date.getTimezoneOffset() * 60000))/1000
                
                events.push({
                    ...this.selectedRecur,
                    id: new UUID().ID(),
                    groups: [...this.selectedRecur.groups],
                    students: [...this.selectedRecur.students],
                    date: from,
                    from,
                    to,
                })
            }
            else {
                const start = dateFromISODate(this.selectedRecur.date)
                let end = dateFromISODate(this.until)

                let recurForDays

                switch (this.selectedRecur.recurSettings.limitType) {
                    case null:
                        break
                    case 'WEEKS':
                        recurForDays = this.selectedRecur.recurSettings.limitSize * 7
                        break
                    case 'DAYS':
                        recurForDays = this.selectedRecur.recurSettings.limitSize
                        break
                    case 'EVENTS':
                        recurForDays = Number(this.selectedRecur.recurSettings.period) * this.selectedRecur.recurSettings.limitSize
                        break
                    default:
                        recurForDays = null
                        break
                }

                if (recurForDays) {
                    end = new Date(start)
                    end.setDate(end.getDate() + recurForDays)
                }
                
                let i = 0
                let from, to

                for (let date = new Date(start); date < end; date.setDate(date.getDate() + 1)) {
                    if (i % this.selectedRecur.recurSettings.period === 0) {
                        from = (new Date(date).setHours(...fromParts).valueOf() - (date.getTimezoneOffset() * 60000))/1000
                        to = (new Date(date).setHours(...toParts).valueOf() - (date.getTimezoneOffset() * 60000))/1000

                        events.push({
                            ...this.selectedRecur,
                            id: new UUID().ID(),
                            groups: [...this.selectedRecur.groups],
                            students: [...this.selectedRecur.students],
                            date: from,
                            from: from,
                            to: to,
                        })
                    }

                    i++
                }
            }

            this.$emit('applyRecur', this.selectedRecur.uid, events)
        },
        selectRecur(recurEvent) {
            if (!recurEvent.ttsessiontype) recurEvent.ttsessiontype = recurEvent.ttsessiontypes[0]
            
            const ttsessiontype = this.sessionTypes.find(st => st.id == recurEvent.ttsessiontype.id)
            const ttroom = ttsessiontype.ttrooms.find(r => recurEvent.ttroom && r.id == recurEvent.ttroom.id)
            const groups = recurEvent.groups.map(g => this.groups.find(gr => gr.group_id == g.id && gr.year == g.year && gr.programme_id == g.programme_id))

            this.selectedRecur = {
                ...recurEvent,
                from: isoTimeFromTimestamp(recurEvent.from),
                to: isoTimeFromTimestamp(recurEvent.to),
                ttsessiontype,
                ttroom,
                groups,
                recur: recurEvent.limit > 1
            }

            if (recurEvent.uid) this.applyRecurEvent()
            if (this.$refs.editor) this.$refs.editor.scrollTo(0, 0)

            this.editRecur = true
        },
        selectNew() {
            this.resetSelected()
            this.editRecur = true
        },
        deriveSettings(recurEvent) {
            return {
                period: recurEvent.period,
                limitType: recurEvent.period % 7 === 0 ? 'WEEKS' : 'DAYS',
                limitSize: recurEvent.limit * recurEvent.period / ((recurEvent.period % 7 === 0) ? 7 : 1)
            }
        },
        resetSelected() {
            this.selectedRecur = {
                id: 0,
                date: '',
                from: '',
                to: '',
                ttsessiontype: null,
                ttroom: null,
                recur: true,
                groups: [],
                students: [],
                recurSettings: {
                    period: 7,
                    limitType: 'WEEKS',
                    limitSize: 1,
                },
                ttsessiontypes: [],
            }
            if (this.$refs.editor) this.$refs.editor.scrollTo(0, 0)
        },
        showDeleteSetDialog() {
            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('delete-recur-set-question'),
                confirmCallBack: this.deleteSet,
                cancelBtn: true,
            })
        },
        deleteSet() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/delete`, {
                method: 'DELETE',
                data: {
                    id: this.recurEventSet.id,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                this.$emit('setDeleted', this.recurEventSet.id)
                this.$emit('close')
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-deleting-recur-set'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })
        },
        showDeleteEventDialog() {
            this.$store.commit('openDialog', {
                title: this.$t('are-you-sure'),
                content: this.$t('delete-recur-event-question'),
                confirmCallBack: this.deleteEvent,
                cancelBtn: true,
            })
        },
        deleteEvent() {
            if (this.submitting) return
            this.submitting = true

            this.$store.commit('showBlockingSpinner')

            this.$cfetch(`${this.$settings.APIBaseURL}/timetables/recureventsets/${this.rotations ? 'rotations' : 'recurevents'}/delete`, {
                method: 'DELETE',
                data: {
                    id: this.selectedRecur.id,
                }
            }).then(res => {
                if (res.status !== 'SUCCESS') throw res

                delete this.recurEventsById[this.selectedRecur.id]

                if (this.rotations) {
                    this.selectedRecur.ttsessiontypes.forEach(type => {
                        this.$emit('applyRecur', `${this.selectedRecur.id}-${type.id}`, [])
                    })
                }
                else this.$emit('applyRecur', this.selectedRecur.id, [])

                this.$emit('close')
            }).catch(err => {
                this.$log.error.call(this, err)
                this.$store.commit('openDialog', {
                    title: this.$t('something-went-wrong'),
                    content: this.$t('error-deleting-recur-event'),
                })
            }).finally(() => {
                this.$store.commit('hideBlockingSpinner')
                this.submitting = false
            })
        },
        getTextColor(sessionType) {
            if (!sessionType.colourcode) return undefined
            return getTextColor(sessionType.colourcode)
        },
        addRotationSessionType() {
            if (!this.selectedRecur.ttsessiontype) return
            
            let index = this.selectedRecur.ttsessiontypes.findIndex(t => t.id === this.selectedRecur.ttsessiontype.id)

            if (index === -1) {
                this.selectedRecur.ttsessiontypes.push({
                    ...this.selectedRecur.ttsessiontype,
                    size: this.sessionSize,
                })
            } else {
                this.selectedRecur.ttsessiontypes[index].size = this.sessionSize
            }

            this.rotationRooms = [ ...this.selectedRecur.ttsessiontype.ttrooms ]
            
            this.selectedRecur.ttsessiontype = null
        },
        recurEventValid() {
            return this.selectedRecur.date 
            && this.selectedRecur.from 
            && this.selectedRecur.to 
            && this.selectedRecur.groups.length
            && ((!this.rotations && this.selectedRecur.ttsessiontype) 
            || (this.rotations && this.selectedRecur.ttsessiontypes.length > 1))
        }
    },
    watch: {
        recurEventSet() {
            this.fetchRecurEvents()
            this.fetchRotations()
        },
        from(newFrom) {
            Object.values(this.processEvents(Object.values(this.recurEventsById))).forEach(this.selectRecur)
            Object.values(this.processRotations(Object.values(this.rotationsById))).forEach(this.selectRecur)
            if (this.recurEventSet && newFrom < this.until) this.saveSet(false)
        },
        until(newUntil) {
            Object.values(this.processEvents(Object.values(this.recurEventsById))).forEach(this.selectRecur)
            Object.values(this.processRotations(Object.values(this.rotationsById))).forEach(this.selectRecur)
            if (this.recurEventSet && newUntil > this.from) this.saveSet(false)
        }
    }
}
</script>